import { useEffect, useState } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { LogOut, LayoutDashboard, ShoppingCart, BookOpen, FileText, Users, Building2, Wallet, MessageSquareWarning, Settings2, ClipboardList, CheckSquare, Package, Sparkles, Briefcase, UserCog, Phone, Mail, Menu, X, FileSpreadsheet, Trash2 } from "lucide-react";

const ROLE_LABEL = {
  employee: "Pracownik",
  manager: "Kierownik",
  office_land_admin: "Office-Land Admin",
};

function NavSection({ title, children }) {
  return (
    <div className="mb-4">
      <p className="px-3 mb-1 text-[10px] font-bold uppercase tracking-widest text-slate-400">{title}</p>
      <div className="space-y-0.5">{children}</div>
    </div>
  );
}

function NavItem({ to, icon: Icon, label, testId, end, onClick }) {
  return (
    <NavLink
      to={to}
      end={end}
      data-testid={testId}
      onClick={onClick}
      className={({ isActive }) =>
        `flex items-center gap-2.5 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
          isActive
            ? "bg-[#FFF4ED] text-[#CC4B00] border-l-2 border-[#FF6B1A] pl-[10px]"
            : "text-slate-600 hover:bg-slate-100 hover:text-slate-900 border-l-2 border-transparent"
        }`
      }
    >
      <Icon className="w-4 h-4" />
      {label}
    </NavLink>
  );
}

function SidebarContact({ icon: Icon, label, name, phone, email, testId }) {
  if (!name && !phone && !email) return null;
  return (
    <div className="px-3 py-2.5 rounded-md bg-slate-50 border border-slate-200" data-testid={testId}>
      <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 flex items-center gap-1 mb-1">
        <Icon className="w-3 h-3" /> {label}
      </p>
      {name && <p className="text-xs font-semibold text-slate-900 truncate" data-testid={`${testId}-name`}>{name}</p>}
      {phone && (
        <a href={`tel:${phone}`} className="mt-1 flex items-center gap-1.5 text-[11px] text-slate-600 hover:text-[#CC4B00] truncate" data-testid={`${testId}-phone`}>
          <Phone className="w-3 h-3 flex-shrink-0 text-slate-400" />
          <span className="font-mono truncate">{phone}</span>
        </a>
      )}
      {email && (
        <a href={`mailto:${email}`} className="flex items-center gap-1.5 text-[11px] text-slate-600 hover:text-[#CC4B00] truncate" data-testid={`${testId}-email`}>
          <Mail className="w-3 h-3 flex-shrink-0 text-slate-400" />
          <span className="truncate">{email}</span>
        </a>
      )}
    </div>
  );
}

function SidebarBody({ user, onNavigate, onLogout, hasContacts }) {
  return (
    <>
      <div className="px-5 py-5 border-b border-slate-200">
        <div className="flex items-center gap-2">
          <div className="w-9 h-9 rounded-md bg-[#FF6B1A] flex items-center justify-center text-white font-bold">OL</div>
          <div>
            <p className="text-sm font-bold tracking-tight text-slate-900 leading-none">Office-Land</p>
            <p className="text-[11px] text-slate-500 mt-0.5">Portal zakupowy · NDI</p>
          </div>
        </div>
      </div>

      <nav className="flex-1 overflow-y-auto px-3 py-4">
        {user?.role === "employee" && (
          <>
            <NavSection title="Zamawianie">
              <NavItem to="/" end onClick={onNavigate} testId="nav-employee-dashboard" icon={LayoutDashboard} label="Dashboard" />
              <NavItem to="/catalog" onClick={onNavigate} testId="nav-catalog" icon={BookOpen} label="Katalog produktów" />
              <NavItem to="/suggestions" onClick={onNavigate} testId="nav-suggestions" icon={Sparkles} label="Sugestie zamówień" />
              <NavItem to="/cart" onClick={onNavigate} testId="nav-cart" icon={ShoppingCart} label="Aktualne zamówienie" />
              <NavItem to="/my-orders" onClick={onNavigate} testId="nav-my-orders" icon={FileText} label="Moje zamówienia" />
            </NavSection>
            <NavSection title="Wnioski">
              <NavItem to="/custom-request" onClick={onNavigate} testId="nav-custom-request" icon={MessageSquareWarning} label="Produkt spoza katalogu" />
              <NavItem to="/budget-request" onClick={onNavigate} testId="nav-budget-request" icon={Wallet} label="Zwiększenie budżetu" />
            </NavSection>
          </>
        )}

        {user?.role === "manager" && (
          <>
            <NavSection title="Akceptacja">
              <NavItem to="/" end onClick={onNavigate} testId="nav-manager-dashboard" icon={LayoutDashboard} label="Dashboard" />
              <NavItem to="/manager/orders" onClick={onNavigate} testId="nav-manager-orders" icon={CheckSquare} label="Zamówienia do akceptacji" />
              <NavItem to="/manager/history" onClick={onNavigate} testId="nav-manager-history" icon={ClipboardList} label="Historia zamówień" />
              <NavItem to="/manager/reports" onClick={onNavigate} testId="nav-manager-reports" icon={FileSpreadsheet} label="Raporty miesięczne" />
            </NavSection>
            <NavSection title="Wnioski">
              <NavItem to="/manager/budget-requests" onClick={onNavigate} testId="nav-manager-budget" icon={Wallet} label="Prośby o budżet" />
              <NavItem to="/manager/custom-requests" onClick={onNavigate} testId="nav-manager-custom" icon={MessageSquareWarning} label="Produkty spoza katalogu" />
            </NavSection>
          </>
        )}

        {user?.role === "office_land_admin" && (
          <>
            <NavSection title="Realizacja">
              <NavItem to="/" end onClick={onNavigate} testId="nav-admin-dashboard" icon={LayoutDashboard} label="Dashboard" />
              <NavItem to="/admin/orders" onClick={onNavigate} testId="nav-admin-orders" icon={ClipboardList} label="Zamówienia zaakceptowane" />
            </NavSection>
            <NavSection title="Zarządzanie">
              <NavItem to="/admin/sites" onClick={onNavigate} testId="nav-admin-sites" icon={Building2} label="Budowy NDI" />
              <NavItem to="/admin/users" onClick={onNavigate} testId="nav-admin-users" icon={Users} label="Użytkownicy" />
              <NavItem to="/admin/products" onClick={onNavigate} testId="nav-admin-products" icon={Package} label="Produkty" />
              <NavItem to="/admin/rules" onClick={onNavigate} testId="nav-admin-rules" icon={Settings2} label="Reguły akceptacji" />
              <NavItem to="/admin/custom-requests" onClick={onNavigate} testId="nav-admin-custom" icon={MessageSquareWarning} label="Produkty spoza katalogu" />
              <NavItem to="/admin/budget-requests" onClick={onNavigate} testId="nav-admin-budget" icon={Wallet} label="Prośby o budżet" />
            </NavSection>
            <NavSection title="System">
              <NavItem to="/admin/cleanup" onClick={onNavigate} testId="nav-admin-cleanup" icon={Trash2} label="Czyszczenie danych" />
            </NavSection>
          </>
        )}
      </nav>

      {hasContacts && (
        <div className="border-t border-slate-200 px-3 py-3 space-y-2">
          <p className="px-1 text-[10px] font-bold uppercase tracking-widest text-slate-400">Kontakty Office-Land</p>
          <SidebarContact
            testId="sidebar-sales-rep"
            icon={Briefcase}
            label="Przedstawiciel handlowy"
            name={user?.sales_rep_name}
            phone={user?.sales_rep_phone}
            email={user?.sales_rep_email}
          />
          {user?.role === "manager" && (
            <SidebarContact
              testId="sidebar-project-manager"
              icon={UserCog}
              label="Project Manager"
              name={user?.project_manager_name}
              phone={user?.project_manager_phone}
              email={user?.project_manager_email}
            />
          )}
        </div>
      )}

      <div className="border-t border-slate-200 px-3 py-3">
        <div className="px-2 mb-2">
          <p className="text-sm font-semibold text-slate-900 truncate" data-testid="user-name">
            {user?.first_name} {user?.last_name}
          </p>
          <p className="text-xs text-slate-500 truncate">{user?.email}</p>
          <p className="text-[10px] mt-1 inline-block px-1.5 py-0.5 rounded bg-[#FFF4ED] text-[#CC4B00] font-semibold">
            {ROLE_LABEL[user?.role] || user?.role}
          </p>
        </div>
        <button
          onClick={onLogout}
          data-testid="logout-btn"
          className="w-full flex items-center gap-2 px-3 py-2 rounded-md text-sm text-slate-600 hover:bg-slate-100 hover:text-slate-900 transition-colors"
        >
          <LogOut className="w-4 h-4" /> Wyloguj
        </button>
      </div>
    </>
  );
}

export default function AppShell({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);

  // Close drawer on route change
  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  // Lock body scroll when drawer is open
  useEffect(() => {
    document.body.style.overflow = mobileOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [mobileOpen]);

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const hasContacts =
    (user?.role === "employee" || user?.role === "manager") &&
    (user?.sales_rep_name || user?.sales_rep_phone || user?.sales_rep_email ||
      user?.project_manager_name || user?.project_manager_phone || user?.project_manager_email);

  return (
    <div className="min-h-screen flex bg-[#F8FAFC]">
      {/* Desktop sidebar */}
      <aside className="hidden md:flex md:flex-col md:w-64 bg-white border-r border-slate-200 sticky top-0 h-screen">
        <SidebarBody user={user} onLogout={handleLogout} hasContacts={hasContacts} />
      </aside>

      {/* Mobile drawer */}
      {mobileOpen && (
        <>
          <div
            className="md:hidden fixed inset-0 bg-slate-900/50 z-40"
            data-testid="mobile-drawer-backdrop"
            onClick={() => setMobileOpen(false)}
          />
          <aside
            className="md:hidden fixed top-0 left-0 bottom-0 w-72 max-w-[85vw] bg-white border-r border-slate-200 z-50 flex flex-col animate-in slide-in-from-left duration-200"
            data-testid="mobile-drawer"
          >
            <button
              onClick={() => setMobileOpen(false)}
              data-testid="mobile-drawer-close"
              className="absolute top-3 right-3 z-10 text-slate-500 hover:text-slate-900 p-1.5 rounded-md hover:bg-slate-100"
              aria-label="Zamknij menu"
            >
              <X className="w-5 h-5" />
            </button>
            <SidebarBody
              user={user}
              onNavigate={() => setMobileOpen(false)}
              onLogout={handleLogout}
              hasContacts={hasContacts}
            />
          </aside>
        </>
      )}

      <main className="flex-1 min-w-0">
        {/* Mobile top bar */}
        <header className="md:hidden bg-white border-b border-slate-200 px-4 py-3 flex items-center justify-between sticky top-0 z-30">
          <div className="flex items-center gap-2">
            <button
              onClick={() => setMobileOpen(true)}
              data-testid="mobile-menu-btn"
              className="text-slate-700 p-1.5 -ml-1.5 rounded-md hover:bg-slate-100"
              aria-label="Otwórz menu"
            >
              <Menu className="w-5 h-5" />
            </button>
            <div className="w-8 h-8 rounded-md bg-[#FF6B1A] flex items-center justify-center text-white font-bold text-xs">OL</div>
            <p className="text-sm font-bold text-slate-900">Office-Land · NDI</p>
          </div>
          <button onClick={handleLogout} data-testid="logout-btn-mobile" className="text-slate-600 p-1.5">
            <LogOut className="w-5 h-5" />
          </button>
        </header>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 md:py-8 animate-fade-in">
          {children}
        </div>
      </main>
    </div>
  );
}
